import moment from 'moment'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import InfiniteScroll from '../../infinite-scroll/InfiniteScroll'

import { ReactComponent as OutsideLinkIcon } from '../../../assets/images/common/icons/outside-link-icon.svg'
import { ReactComponent as SortIcon } from '../../../assets/images/common/icons/sort-icon.svg'
import ProfileAvatar from '../../common/avatar/ProfileAvatar'
import Loader from '../../common/loader/Loader'
import LoadingSpinner from '../../common/loader/legacy-loaders/LoadingSpinner'

import classes from './StudentsTable.module.scss'
import colors from '../../../assets/styles/globals.scss'

import { genderLabel } from '../../../utility/userHelper'
import { displayWithCurrency } from '../../../utility/currency'
import { getSortStates } from '../../../utility/members'

const FIRST_COLUMN_WIDTH = '320px'

const SortButton = ({ label, active, onClick, loading }) => (
  <button className={classNames(classes.sortButton, { [classes.active]: active })} onClick={onClick} disabled={loading}>
    {label}
    {loading ? (
      <LoadingSpinner classNames={{ wrapper: classes.spinnerWrapper }} />
    ) : (
      <SortIcon className={classes.sortIcon} width={16} height={16} color={colors.slateGrayIcons} />
    )}
  </button>
)

SortButton.propTypes = {
  label: PropTypes.string,
  active: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
}

// eslint-disable-next-line max-lines-per-function
const StudentsTable = ({
  students, fetchData, pagination, onStudentClick, inspectedStudentId, isExporting, isLoading, isSortLoading, sortOption, applySort, visibleColumns,
}) => {
  const hasMore = !pagination?.totalPages || (pagination?.page + 1) <= pagination?.totalPages
  const currency = useSelector(state => state.user.current_user.communities[0].currency)

  const gridTemplateColumns = `${FIRST_COLUMN_WIDTH} ${visibleColumns.map(c => c.gridWidth).join(' ')}`

  if (!students || !pagination) {
    return (
      <div className='h-100 d-flex align-items-center justify-content-center'>
        <Loader>Loading Students</Loader>
      </div>
    )
  }

  const getNextSortValue = (selectedSortField, currentSortValue) => {
    const states = getSortStates(selectedSortField)

    if (selectedSortField !== sortOption.sortField) {
      return states.at(0)
    }

    if (currentSortValue === states.at(-1)) {
      return null
    }

    const currentIndex = states.indexOf(currentSortValue)
    return states[(currentIndex + 1) % states.length]
  }

  return (
    <div id='studentsTable' className={classes.table}>
      <InfiniteScroll
        dataLength={students.length}
        next={fetchData}
        hasMore={hasMore}
        scrollableTarget='tableContent'
        className={classes.tableContent}
        hideLoader
      >
        <div className={classes.gridTable} id='tableContent' style={{ gridTemplateColumns }}>
          <div className={classNames(classes.col, classes.header, classes.firstColumn, classes.topLeft)}>
            <span className='font-weight-bold'>Students ({pagination?.totalCount})</span>
          </div>
          {visibleColumns.map((col) => (
            <div className={classNames(classes.col, classes.header)} key={col.field}>
              <SortButton
                label={col.label}
                active={sortOption.sortField === col.field && sortOption.sortValue !== null}
                onClick={() => applySort(col.field, getNextSortValue(col.field, sortOption.sortValue))}
                loading={isSortLoading && sortOption.sortField === col.field}
              />
            </div>
          ))}

          {isExporting ? (
            <div className={classes.exportLoadingWrapper}>
              <Loader loaderType='ball-beat' loaderColor={colors.lightNavy}>Exporting Members</Loader>
              <span className='text-center'>Depending on community size this may take several minutes.</span>
            </div>
          ) : (
            <>
              {students.map(student => (
                <div
                  tabIndex={0}
                  key={student.id}
                  role='button'
                  className={classNames(classes.row, { [classes.isClicked]: student.id === inspectedStudentId })}
                  onClick={() => onStudentClick(student)}
                  onKeyDown={() => null}
                >
                  <div
                    className={classNames(classes.col, classes.firstColumn, classes.studentInfo)}
                    title={`${student.firstName} ${student.lastName}`}
                  >
                    <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
                    <div className='d-flex flex-column flex-grow-1 pr-3'>
                      <h6 className='mb-1 font-weight-bold'>{student.firstName} {student.lastName}</h6>
                      <span>
                        {`${student.age ?? '-'}yrs / `}
                        {genderLabel[student.gender]}
                      </span>
                    </div>
                    <OutsideLinkIcon className={classes.detailsIcon} />
                  </div>

                  {visibleColumns.map((col) => (
                    <div className={classNames(classes.col, classes.centeredCol)} key={col.field}>
                      {col.field === 'first_activity' && (
                        <span className='font-weight-bold'>{student.firstActivity ? moment(student.firstActivity).fromNow(true) : '-'}</span>
                      )}
                      {col.field === 'enrollment_status' && (
                        <span
                          className={classNames(
                            classes.enrollmentStatus,
                            {
                              [classes.enrollmentActive]: student.enrollmentStatus === 'active',
                              [classes.enrollmentFuture]: student.enrollmentStatus === 'future',
                              [classes.enrollmentWaitlist]: student.enrollmentStatus === 'waitlist',
                            }
                          )}>
                          {student.enrollmentStatus}
                        </span>
                      )}
                      {col.field === 'total_enrollments' && <span className='font-weight-bold'>{student.enrollmentsCount}</span>}
                      {col.field === 'total_revenue' && <span className='font-weight-bold'>
                        {student.totalRevenue > 0 ? displayWithCurrency(student.totalRevenue, currency, true) : '-'}
                      </span>}
                      {col.field === 'strokes_lost' && <span className='font-weight-bold'>{student.strokesLost}</span>}
                      {col.field === 'nine_hole_rounds' && <span className='font-weight-bold'>{student.nineHoleRoundsCount}</span>}
                      {col.field === 'casual_rounds' && <span className='font-weight-bold'>{student.casualRoundsCount}</span>}
                      {col.field === 'practice_sessions' && <span className='font-weight-bold'>{student.practiceSessionsCount}</span>}
                      {col.field === 'time_practiced' && <span className='font-weight-bold'>{student.timePracticed}</span>}
                      {col.field === 'age' && <span className='font-weight-bold'>{student.age}</span>}
                      {col.field === 'gender' && <span className='font-weight-bold'>{student.genderLabel}</span>}
                      {col.field === 'level' && <span className='font-weight-bold'>{student.currentFormalDivision}</span>}
                      {col.field === 'latest_program_enrolled' && <span className='font-weight-bold'>{student.latestProgramEnrolled || '-'}</span>}
                    </div>
                  ))}
                </div>
              ))}

              {!!(hasMore && isLoading) && Array.from({ length: 3 }, (_, idx) => (
                <div className={classNames(classes.row, classes.skeletons)} key={`skeleton-${idx}`}>
                  <div
                    tabIndex={0}
                    role='button'
                    onKeyDown={() => null}
                    className={classNames(classes.col, classes.firstColumn, classes.studentInfo)}
                  >
                    <div data-skeleton='avatar' />
                    <div className={classNames('d-flex flex-column flex-grow-1 pr-3', classes.skeletonInfoWrapper)}>
                      <div data-skeleton='name' />
                      <div data-skeleton='info' />
                    </div>
                  </div>

                  {visibleColumns.map((col) => (
                    <div className={classNames(classes.col, classes.centeredCol)} key={col.field}>
                      <div data-skeleton={col.skeletonType} />
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </InfiniteScroll>
      {visibleColumns.length === 0 && (
        <div className={classes.noColumnsMessage}>Please turn on a column to view data</div>
      )}
    </div>
  )
}

StudentsTable.propTypes = {
  students: PropTypes.array,
  fetchData: PropTypes.func.isRequired,
  onStudentClick: PropTypes.func,
  pagination: PropTypes.object,
  inspectedStudentId: PropTypes.number,
  isExporting: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSortLoading: PropTypes.bool,
  sortOption: PropTypes.object,
  applySort: PropTypes.func,
  visibleColumns: PropTypes.array.isRequired,
}

export default StudentsTable
